// Public, UNAUTHENTICATED one-tap sign-out page — reached via the link emailed on
// sign-in (or the end-of-day "still on site?" reminder). Lets the person sign out
// from anywhere, and set the actual time they left rather than "now" if it's a
// forgotten sign-out picked up later in the day.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, LogOut, Loader2, AlertCircle, HardHat } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}
function toLocalInput(d) {
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export default function AttendanceSignOutPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [time, setTime] = useState(toLocalInput(new Date()));
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    useEffect(() => {
        axios.get(`${API}/public/site-forms/signout/${token}`)
            .then((r) => setInfo(r.data))
            .catch((e) => setError(e.response?.data?.detail || "This sign-out link is invalid or has expired."));
    }, [token]);

    const submit = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/public/site-forms/signout/${token}`, { time_out: new Date(time).toISOString() });
            setDone(r.data);
        } catch (e) {
            setError(e.response?.data?.detail || "Could not sign you out.");
        } finally { setBusy(false); }
    };

    const brand = info?.primary_colour || "#C75A1B";
    const where = [info?.project_name, info?.site_name].filter(Boolean).join(" · ") || info?.client_name;

    return (
        <div className="min-h-screen bg-[#F5F3F0] py-6 px-4" data-testid="attendance-signout-page">
            <div className="max-w-md mx-auto">
                <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-lg grid place-items-center text-white" style={{ background: brand }}><HardHat size={18} /></div>
                    <div>
                        <div className="text-[13px] font-bold text-[#1F1F1F]">{info?.client_name || "Priority Risk"}</div>
                        <div className="text-[11px] text-[#9A968F]">{where}</div>
                    </div>
                </div>

                <div className="rounded-[16px] border shadow-sm overflow-hidden bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <div className="px-5 py-4" style={{ background: brand }}>
                        <h1 className="text-lg font-bold text-white">Sign out</h1>
                    </div>
                    <div className="p-5 space-y-4">
                        {error && (
                            <div data-testid="attendance-signout-error" className="flex items-start gap-2 text-[13px] text-red-600">
                                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /><span>{error}</span>
                            </div>
                        )}
                        {!error && !info && <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#9A968F]" /></div>}

                        {info && !done && (
                            info.signed_out ? (
                                <div data-testid="attendance-already-signed-out" className="text-center py-4 space-y-2">
                                    <CheckCircle2 size={30} className="mx-auto text-green-600" />
                                    <p className="text-[14px] font-semibold text-[#1F1F1F]">{info.name} is already signed out</p>
                                    <p className="text-[12.5px] text-[#6F6A64]">Signed out {fmt(info.time_out)}.</p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-[13.5px] text-[#1F1F1F]">Hi {(info.name || "").split(" ")[0]} — you're signed in since <strong>{fmt(info.time_in)}</strong>.</p>
                                    <label className="block">
                                        <span className="text-[12.5px] font-semibold text-[#1F1F1F]">What time did you actually leave?</span>
                                        <input data-testid="attendance-signout-time" type="datetime-local" value={time} onChange={(e) => setTime(e.target.value)}
                                            className="w-full h-11 px-3 rounded-lg border border-[#E5E0DA] bg-white text-[15px] mt-1.5 focus:outline-none" />
                                    </label>
                                    <button data-testid="attendance-signout-submit" onClick={submit} disabled={busy}
                                        className="w-full h-11 rounded-[10px] font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60"
                                        style={{ background: brand }}>
                                        {busy ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />} Sign out
                                    </button>
                                </>
                            )
                        )}

                        {done && (
                            <div data-testid="attendance-signout-done" className="text-center py-4 space-y-2">
                                <CheckCircle2 size={30} className="mx-auto text-green-600" />
                                <p className="text-[14px] font-semibold text-[#1F1F1F]">Signed out — thanks, {(done.name || "").split(" ")[0]}.</p>
                                <p className="text-[12.5px] text-[#6F6A64]">Recorded at {fmt(done.time_out)}.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
